import { pickBy } from 'es-toolkit';
import queryString from 'query-string';

import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import { shippingProviders, ShippingPartnerIds } from '@/enums/shippingProviders';
import { geoAreaToUrlPath } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { CoverLot, EmptyCoverLot } from '@/types/CoverLot';
import { GeoArea } from '@/types/GeoArea';
import { ItemFacets } from '@/types/ItemFacets';
import { SearchParams } from '@/types/SaveSearch';
import { SearchUrlStatus } from '@/types/search/enums/searchUrlStatus';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchQueryStatus } from '@/types/search/searchQuery';
import { parseSearchSeoPath, SEARCH_SEO_SUBJECT_PATH_SEGMENT } from '@/utils/search/searchSeoPath';
import { translateAccents } from '@/utils/translateCharacterAccents';

import { cleanKeywordSearchString } from './cleanSearchTerms';
import { DEFAULT_STATUS_FOR_ONLINE_SEARCH, parseQueryParams } from './queryParams';
import { sanitizeHtml } from './sanitize';
import { addOrsBetweenKeywords } from './strings';

export const imagePlaceholder = 'https://p1.liveauctioneers.com/dist/images/placeholder.jpg';

/**
 * Removes URL query string and hash fragment. Used for rel=canonical so facet/filter
 * query params (e.g. house_id) are not emitted in metadata.
 */
export const stripUrlQueryAndHash = (url: string): string => {
    if (!url) {
        return url;
    }
    let result = url;
    const hashIndex = result.indexOf('#');
    if (hashIndex !== -1) {
        result = result.slice(0, hashIndex);
    }
    const queryIndex = result.indexOf('?');
    if (queryIndex !== -1) {
        result = result.slice(0, queryIndex);
    }
    return result;
};

/**
 * Normalizes paths so the same URL compares equal across the places it reaches us in
 * different shapes: SSR passes `request.originalUrl` (basename, query and hash included),
 * the client passes a bare `location.pathname`.
 */
export const normalizeSoft404Path = (path: string = ''): string => {
    let pathname = (stripUrlQueryAndHash(path).replace(/\/+$/, '') || '/').toLowerCase();

    if (pathname === '/en-gb') {
        return '/';
    }

    if (pathname.startsWith('/en-gb/')) {
        pathname = pathname.slice('/en-gb'.length) || '/';
    }

    return pathname;
};

/**
 * Normalize a string for use in URLs (lowercase, replace spaces with hyphens)
 */
export const normalizeForUrl = (str: string): string => {
    return str.toLowerCase().replace(/\s+/g, '-');
};

export const getCategoryIdsFromPathname = (pathname: string = '') => {
    if (pathname === '/catalog/search') {
        return '0';
    }
    return pathname
        .split('/')
        .filter((x) => Number(x))
        .join(',');
};

export const getCombinationCategoryIdsFromPath = (params: any, queryParams: any) => {
    if (!params.categoryId2) {
        return '';
    }
    let ids = [params.categoryId, params.categoryId2];
    for (let key in queryParams) {
        ids.push(queryParams[key]);
    }
    ids.sort(function (a, b) {
        return a - b;
    });
    return ids.join(',');
};

// gets the last categoryid from the path
export const getFinalCategoryIdFromPathname = (pathname: string = '') => {
    if (pathname === '/catalog/search') {
        return '';
    }
    const categoryIds = pathname.split('/').filter((x) => Number(x));
    if (categoryIds.length > 0) {
        return categoryIds[categoryIds.length - 1];
    }
    return '';
};

// retrieves the last string from a path, which in some scenarios will be the lowest-level category desired
// returns category name formatted to be passed for wordpress api search
export const getCategoryNameFromPathname = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    const cleanedUpCategoryName = categoryName.length ? categoryName[categoryName.length - 1].replace(/-/g, ' ') : '';
    return cleanedUpCategoryName;
};

export const getRawCategoryNameFromPathname = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    return categoryName[categoryName.length - 1];
};

export const getIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }

    const pathString = `${path}`;
    const cleaned = pathString.replace(
        /^\/?(catalog(_gallery)?|item|auctioneer|catinfot|console|checkoutATG|checkout|terms)\/?/,
        ''
    );

    let urlFrags = cleaned.split('_')[0];

    if (urlFrags.includes('/page')) {
        urlFrags = urlFrags.split('/')[0];
    }

    const idMatches = urlFrags.match(/^(\d+)\/?$/);
    return idMatches ? Number(idMatches[1]) || 0 : 0;
};

export const getItemIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }

    const pathString = `${path}`.toLowerCase();
    const itemIdMatches = pathString.match(/item\/(\d+)/);

    return itemIdMatches !== null ? Number(itemIdMatches[1]) : 0;
};

// https://stackoverflow.com/a/1714899/148256
// Returns a string from the object
export const toQueryString = (obj: any) => {
    const arr: string[] = [];

    // Checks if object has prototypes, if not, creates a new object with default prototypes
    let params = obj;
    if (Boolean(obj) && !obj.__proto__) {
        params = { ...obj };
    }

    for (const p in params) {
        if (params.hasOwnProperty(p)) {
            arr.push(`${encodeURIComponent(p)}=${encodeURIComponent(params[p])}`);
        }
    }
    arr.sort();
    return arr.join('&');
};

export const sellerIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }
    let sellerId = Number(path);

    if (!sellerId) {
        sellerId = Number(path.split('-')[0]);
    }
    return sellerId;
};

export const logoUrl = 'https://p1.liveauctioneers.com/dist/images/logo_og.jpg';
export const signupLadySmallUrl = 'https://p1.liveauctioneers.com/dist/images/SignupLadySmall.png';
export const mobileLogo = 'https://p1.liveauctioneers.com/dist/images/logo-1024.png';
export const enableBrowserNotificationsUrl =
    'https://help.liveauctioneers.com/hc/en-gb/articles/24746619899409-How-to-enable-browser-notifications';
export const urlSafeTitle = (title: string = '') => {
    let cleanedAndDecodedHtml = sanitizeHtml(title);
    cleanedAndDecodedHtml = translateAccents(cleanedAndDecodedHtml);
    const safe = cleanedAndDecodedHtml
        .trim()
        .toLowerCase() // & to and
        .replace(/&/g, 'and') // Replace , * ; : ' " to hyphen
        .replace(/[,*;:'"]+/g, '-') // Replace `- -` to single hyphen
        .replace(/-*\s+-*/g, '-') // Remove special characters
        .replace(/[^a-zA-Z0-9-_-]/g, '') // many hyphen to one
        .replace(/--+/g, '-') // Remove hyphen if it is the last character
        .replace(/-+$/, '');
    const urlEncoded = encodeURIComponent(safe).toLowerCase(); // lower case it again since the URI encoded values can be upper case
    return urlEncoded;
};

export const buildQueryUrl = (base, params?: any): string => {
    const queryString = toQueryString(params);
    return queryString === '' ? base : `${base}?${queryString}`;
};

export const getAuctionCalendarBaseUrl = () => '/catalog/search/';

export const getAuctionCalendarUrlWithParams = (params?: any) => {
    const base = getAuctionCalendarBaseUrl();
    return buildQueryUrl(base, params);
};

export const getSearchBaseUrl = () => '/search/';

export const getCollectionsBaseUrl = (keyword: string) => `/collections/${keyword}/`;
const getSeoPageBaseUrl = (keyword: string, subjectPathSlug?: string) => {
    const subjectTrail = subjectPathSlug ? `/${SEARCH_SEO_SUBJECT_PATH_SEGMENT}/${subjectPathSlug}` : '';
    return `/search/${keyword}${subjectTrail}/`;
};
const getLocalizedSearchPageBaseUrl = (region: string, citySlug: string, keyword: string, subjectPathSlug?: string) => {
    const subjectTrail = subjectPathSlug ? `/${SEARCH_SEO_SUBJECT_PATH_SEGMENT}/${subjectPathSlug}` : '';
    return `/search/${region}/${citySlug}/${keyword}${subjectTrail}/`;
};
export const getSearchUrlWithParams = (params: SearchUrlParams) => {
    const base = getSearchBaseUrl();
    return buildQueryUrl(base, params);
};

export const getSeoSearchUrlWithParams = (params: SearchUrlParams, subjectPathSlug?: string) => {
    const { artSubject: _artSubject, ...queryParams } = params as SearchUrlParams & Record<string, unknown>;
    const base = getSeoPageBaseUrl(params?.keyword ?? '', subjectPathSlug);
    return buildQueryUrl(base, queryParams);
};

export const getSeoSearchUrlWithLocationParams = (params: SearchUrlParams, subjectPathSlug?: string) => {
    const { artSubject: _artSubject, ...queryParams } = params as SearchUrlParams & Record<string, unknown>;
    const base = getLocalizedSearchPageBaseUrl(
        params?.region ?? '',
        params?.citySlug ?? '',
        params?.keyword ?? '',
        subjectPathSlug
    );
    return buildQueryUrl(base, queryParams);
};

export const getCollectionsUrlWithParams = (params: SearchUrlParams) => {
    const base = getCollectionsBaseUrl(params?.keyword ?? '');
    return buildQueryUrl(base, params);
};

export const addParamsToSearchUrl = (searchUrl: string, params: Record<string, string | number | boolean>): string => {
    // baseUrl is here just to give new URL a 'proper' url to parse
    const baseUrl = 'https://www.liveauctioneers.com/';
    const url = new URL(searchUrl, baseUrl);
    let queryParams = queryString.parse(url.search.substring(1));

    // Add all provided parameters
    Object.entries(params).forEach(([key, value]) => {
        queryParams[key] = value.toString();
    });

    return `${url.pathname}?${queryString.stringify(queryParams)}`;
};

export const getCategoryUrlWithParams = (base: string, params: any) => {
    return buildQueryUrl(base, params);
};

export const getCategoryFacetLink = (base: string, params: any) => {
    const path = base === '' || base === undefined ? '/c/' : `/c/${base}/`;
    return buildQueryUrl(path, params);
};

//keywords: string could just be 'rings' or could be 'paintings/oil'
//categoryId: string. Could be '96' or could be '10,25830'(which is a subcategory)
export const getCategoryPageUrl = (keywords: string, categoryId: string, params?: any): string => {
    const url = `/c/${keywords}/${categoryId.replace(',', '/')}/`;
    return buildQueryUrl(url, params);
};

export const getSimilarItemsPageUrlWithParams = (base: string, params: any) => {
    const url = base.replace(/\/(c|search)\//, '/similar-items/');
    return buildQueryUrl(url, params);
};

export const getCombinedCategoryPageUrl = (
    firstCategory: { id: string | number; name: string },
    secondCategory: { id: string | number; name: string }
) => {
    const url = `/c/${urlSafeTitle(firstCategory.name)}/${urlSafeTitle(
        secondCategory.name
    )}/${firstCategory.id}/${secondCategory.id}/`;
    return buildQueryUrl(url);
};

export const getACHTermsAndConditionsUrl = () => '/achtermsandconditions';
export const getAuctionResultsUrl = () => '/auction-results';
export const getAuctioneerDirectoryUrl = () => '/auctioneer/directory/';
export const getCatalogUrl = (id: number, title: string) => {
    const slug = urlSafeTitle(title);
    // An empty slug would leave a dangling underscore. Note that neither shape avoids the
    // canonical redirect in catalogPageData; only a real title does.
    return slug ? `/catalog/${id}_${slug}/` : `/catalog/${id}/`;
};
export const getCategoryLandingPageBaseUrl = () => '/c/';
export const getCategoryLandingPageUrl = (categoryName: string, categoryId: number) =>
    `/c/${urlSafeTitle(categoryName)}/${categoryId}/`;
export const isCategoryPage = (path: string = '') => path.includes(getCategoryLandingPageBaseUrl());
export const getCatalogTermsUrl = (id: number, title: string) => `/terms/${id}_${urlSafeTitle(title)}`;
export const getCheckoutUrl = (catalogId: number) => `/checkout/${catalogId}`;
export const getCheckoutUrlATG = (catalogId: number) => `/checkoutATG/${catalogId}`;
export const getConsignmentsUrl = () => '/consignments';
export const getConsignmentsSellerUrl = (sellerId: number) => `/consignments/${sellerId}`;
export const getCookiePolicyUrl = () => '/cookie-policy';
export const getDiscoverPageUrl = (topic: string) => addTrailingSlash(`/discover/${topic}`);
export const getDisputeHelpUrl = () => 'https://help.liveauctioneers.com/article/163-how-to-handle-a-dispute';
export const getFeaturedCatalogsUrl = () => '/featured-auctions';
export const getFollowedSearches = (append?: string) => `/dashboard/followed-searches/${append ? `${append}/` : ''}`;
export const getFollowedSellersForYouUrl = () => '/dashboard/followed-auctioneers-for-you';
export const getForYouUrl = (append?: string) => `/dashboard/for-you/${append ? `${append}/` : ''}`;
export const getSimilarItemAlertsUrl = () => '/dashboard/similar-item-alerts';
export const getHomeUrl = () => '/';
export const getHowAuctionsWorkUrl = () => '/how-auctions-work';
export const getHowTimedAuctionsWorkUrl = () =>
    'https://support.liveauctioneers.com/hc/en-gb/articles/24574071591313-How-do-Timed-auctions-work';
export const getIntellectualPropertyUrl = () => '/intellectualproperty';
export const getItemUrl = (id: number, title: string) => (id ? `/item/${id}_${urlSafeTitle(title)}` : '');
export const getItemUrlFromSlug = (id: number, slug: string) => `/item/${id}_${slug}`;
export const isItemPage = (path: string = '') => path.includes('/item/');
export const isCatalogPage = (path: string = '') => path.toLowerCase().includes('/catalog/');
export const getLiveBiddingUrl = (catalogId: number) => `/console/${catalogId}`;
export const getAuctionVideoUrl = (catalogId: number) => `/video/${catalogId}`;
export const getConsoleImageCarouselUrl = (catalogId: number, itemId: number, imageNum: number) =>
    `/console/${catalogId}/${itemId}/images/${imageNum}`;
export const getMessagesUrl = () => '/dashboard/messages';
export const getMyBidsUrl = () => '/dashboard/my-bids';
export const getNotFoundUrl = () => '/not-found';
export const getNotificationsUrl = (section?: string) =>
    `/dashboard/emails-and-notifications${section ? `?section=${section}` : ''}`;
export const getRecommendedItemsUrl = () => '/recommended';
export const getRecentItemsUrl = () => '/recent';
export const getReviewUrl = (catalogId: number) => `/review/${catalogId}`;
export const getPreferencesUrl = () => '/dashboard/profile?tab=preferences';
export const getPriceGuideBaseUrl = () => '/price-guide/';
export const isPriceGuidePage = (path: string = '') => path.includes(getPriceGuideBaseUrl());
export const getPriceGuideUrl = (name: string, id: number) => `${getPriceGuideBaseUrl()}${urlSafeTitle(name)}/${id}/`;
export const isPriceResultsPage = (path: string = '') => path.includes('/price-result/');
export const getPurchaseProtectionUrl = () => '/purchase-protection';
export const getPriceResultsUrl = (slug: string, id?: number) =>
    id ? `/price-result/${slug}/?itemId=${id}` : `/price-result/${slug}/`;
export const getPrivacyUrl = () => '/privacy';
export const getProfileUrl = (provider?: number) =>
    provider ? `/dashboard/profile?provider=${provider}` : '/dashboard/profile';
export const getSavedItemsUrl = () => '/dashboard/saved-items';
export const getIsCollectionsPage = (path: string = '') => path.includes('/collections/');
export const getIsSearchPage = (path: string = '') => path.includes('/search/');
export const isVisualSearchPage = (path: string = '') => path.includes('/visual-search');
// /search/:keyword/ or /search/:keyword/subject/:slug/ (optional subject tail)
export const getIsSEOSearchPage = (path: string = '') => {
    if (!path.includes('/search/')) {
        return false;
    }
    const parsed = parseSearchSeoPath(path);
    return Boolean(parsed?.kind === 'plain');
};
// /search/:region/:citySlug/:keyword/ or .../subject/:slug/
export const getIsSEOSearchPageWithLocation = (path: string = '') => {
    if (!path.includes('/search/')) {
        return false;
    }
    const parsed = parseSearchSeoPath(path);
    return Boolean(parsed?.kind === 'location');
};
// The `/similar/:itemId` See Similar Items page. Distinct from the `/similar-items/` category
// routes below (isSimilarItemsPage): `/similar/` requires the trailing slash, so it never matches
// `/similar-items/...`.
export const isSimilarPage = (path: string = '') => path.includes('/similar/');
export const isSimilarItemsPage = (path: string = '') => path.includes('/similar-items/');
// Single source of truth for the routes where an item card hides its Timed+ "Register for Auction"
// button: results grids (keyword search, category, visual search, similar) and the item page. You
// register from the item page there, not the card. Add new results-grid routes here so the button
// doesn't leak onto them.
export const isRegisterButtonHiddenPage = (path: string = '') =>
    isCategoryPage(path) ||
    isItemPage(path) ||
    getIsSearchPage(path) ||
    isVisualSearchPage(path) ||
    isSimilarPage(path);
export const isNotificationsPage = (path: string = '') => path.includes('/dashboard/emails-and-notifications');

/**
 * Whether the post-save app download promo may show on this path.
 * Centralizes route exclusions; save can occur on many surfaces (item PDP, console, search grids,
 * My Items, price-result carousels, etc.) — see ItemCardSaveIcon and toggleItemSave call sites.
 */
export function canShowAppDownloadPromoOnPath(pathname: string): boolean {
    return !isNotificationsPage(pathname);
}
export const isMyBidsPage = (path: string = '') => path.includes('/my-bids');
export const isSavedItemsPage = (path: string = '') => path.includes('/saved-items');
export const getSellPageUrl = () => 'https://www.liveauctioneers.com/pages/sell/';
export const getSellerUrl = (id: number, title: string) => `/auctioneer/${id}/${urlSafeTitle(title)}/`;
export const getSellerReviewsUrl = (id: number, title: string, filter: string = '') =>
    filter
        ? `/auctioneer/${id}/${urlSafeTitle(title)}/reviews/?filter=${filter}`
        : `/auctioneer/${id}/${urlSafeTitle(title)}/reviews/`;
export const getStoreFrontUrl = (sellerId: number) => `/seller/${sellerId}`;
export const getSupportUrl = () => 'https://help.liveauctioneers.com';
export const getTerminologyUrl = () => '/terminology';
export const getTermsAndConditionsUrl = (anchor?: string) =>
    anchor ? `/termsandconditions#${anchor}` : '/termsandconditions';
export const getTermsAndConditionsPaymentsUrl = () => '/termsandconditionspayments';
export const getSellerTermsAndConditionsPaymentsUrl = () =>
    'https://support.liveauctioneers.com/article/92-liveauctioneers-seller-terms-conditions';
export const getWonItemsPageUrl = () => '/dashboard/won-items';
export const getTaxCertManagementUrl = () => '/dashboard/profile?tab=tax-cert-management';
export const getManageCategoryNotificationsUrl = (bidderEmail: string) =>
    `https://link.liveauctioneers.com/manage/4x3/category-management-v2?email=${bidderEmail}`;

export const getJewelryShippingInsuranceUrl = () => 'https://www.jewelersmutual.com/liveauctioneers';

export const getAuctioneerLogoUrl = (logoId: string = '', width: number = 110, sharpen: boolean = false) => {
    return (
        logoId &&
        `https://images.liveauctioneers.com/houses/logos/lg/${logoId}_large.jpg?width=${width}&quality=80${sharpen ? '&sharpen=true' : ''}`
    );
};
export const getGuideArticleUrl = (topic: string, slug: string): string => {
    return `/guides/${topic}/${slug}`;
};

/**
 * True for `/guides/:topic/:slug` (optional trailing slash, query/hash, `/en-gb` basename).
 * SSR passes `request.originalUrl`, so this must tolerate more than a bare pathname.
 * Arbitrary prefixes (e.g. `/legacy/foo/guides/...`) are rejected.
 */
export const isGuideArticlePage = (path: string = ''): boolean => {
    const pathname = (stripUrlQueryAndHash(path).replace(/\/+$/, '') || '/').toLowerCase();
    return /^\/(?:en-gb\/)?guides\/[^/]+\/[^/]+$/.test(pathname);
};

export const getAuctioneerLogoUrlPlaceholder = (auctioneer: string | null | undefined = '', width: number = 110) => {
    return `${getAuctioneerLogoUrl(auctioneer, width)}&quality=15`;
};

export const getAllUpcomingAuctionsUrl = () => getAuctionCalendarUrlWithParams();

export type GetImageUrlParams = {
    blur?: any;
    catalogId?: number;
    crop?: any;
    height?: number;
    imageNumber?: number;
    imageVersion?: number;
    itemId?: number;
    quality?: any;
    sellerId?: number;
    sharpen?: boolean;
    width?: number;
};

export const getImageUrl = ({
    blur,
    catalogId,
    crop,
    height,
    imageNumber = 1,
    imageVersion = 1,
    itemId,
    quality,
    sellerId,
    sharpen,
    width,
}: GetImageUrlParams) => {
    if (!sellerId || !catalogId || !itemId || !imageNumber) {
        return '';
    }

    return addImageParams({
        blur,
        crop,
        height,
        quality,
        sharpen,
        url: `https://p1.liveauctioneers.com/${sellerId}/${catalogId}/${itemId}_${imageNumber}_x.jpg`,
        version: imageVersion,
        width,
    });
};

export type AddImageParamTypes = {
    blur?: any;
    crop?: any;
    height?: number;
    quality?: number;
    sharpen?: boolean;
    url?: string;
    version?: number;
    width?: number;
};

export const addImageParams = ({
    blur,
    crop,
    height,
    quality = 70,
    sharpen,
    url = '',
    version,
    width,
}: AddImageParamTypes) => {
    const query = pickBy(
        {
            blur,
            crop,
            height,
            quality,
            sharpen,
            version,
            width,
        },
        (value) => value !== undefined && value !== null && value !== 0 && value !== false
    );

    if (!url) {
        return '';
    }

    const [path = '', queryParams = ''] = url.split('?');
    const params = (Boolean(queryParams) && parseQueryParams(`?${queryParams}`)) || {};
    return `${path.replace('http://', 'https://')}?${toQueryString({
        ...params,
        ...query,
    })}`.trim();
};

export const getMediumItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1,
    quality: number = 70
) =>
    getImageUrl({
        catalogId,
        height: 512,
        imageNumber,
        imageVersion,
        itemId,
        quality,
        sellerId,
    });

export const getCuratorImageUrl = (
    baseUrl: string,
    curatorId: number,
    curatorImageFileName: string,
    size: number = 280
) =>
    `${baseUrl}/${curatorId}/${encodeURIComponent(curatorImageFileName)}?width=${size}&aspect_ratio=100:101&quality=70`;

export const getItemCardImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number
) =>
    getImageUrl({
        catalogId,
        height: 280,
        imageNumber,
        imageVersion,
        itemId,
        sellerId,
    });

export const getDeskSupportLink = () => 'https://help.liveauctioneers.com/';

export const getAdQuestionUrl = () => 'https://www.surveymonkey.com/r/KFCCVSX';

export const getSearchPageMostPopular = () =>
    getSearchUrlWithParams({
        page: 1,
        sort: SearchSortAndDirection.Popularity,
    });

const categoryLandingIds = {
    art: 1,
    asian: 158,
    collectibles: 2,
    fashion: 3,
    furniture: 4,
    'home-and-decor': 5,
    jewelry: 6,
};

export const getCategoryLandingPageUrlFromCategoryName = (category: string) => {
    const categoryId = categoryLandingIds[category];
    return categoryId
        ? `${getCategoryLandingPageBaseUrl()}${category}/${categoryId}/`
        : getCategoryLandingPageBaseUrl();
};

export const separateURL = (url: string) => {
    const match = url.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match === null) {
        return {
            host: '',
            path: '',
        };
    }
    return {
        host: match[2],
        path: match[5],
    };
};

export const getSearchAuctioneerUpcomingUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        sort: SearchSortAndDirection.Popularity,
        trigger: 'upcoming-items',
    });

export const getSearchAuctioneerUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        sort: SearchSortAndDirection.Popularity,
    });

export const getSearchAuctioneerSoldUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        status: SearchUrlStatus.Archive,
    });

/**
 * getBroadTitleSearchParams
 * returns provided title string separated by OR strings in SearchParams object
 * keyword node to yield a broader search result
 * if sellerId is provided it is added as house_id node, converted to string
 * @param title string
 * @param sellerId number
 * @returns SearchParams
 */
export const getBroadTitleSearchParams = (title: string = '', sellerId: number = 0): SearchParams => {
    const keyword = title
        .split(/\s+/g)
        .map((word) => `${word}`)
        .join(' OR ')
        .replace(/"/g, '\\"');
    const params: SearchParams = {
        keyword,
        status: DEFAULT_STATUS_FOR_ONLINE_SEARCH,
    };
    if (sellerId) {
        params.house_id = sellerId.toString();
    }
    return params;
};

/**
 * getOnTheHuntItemSearchParams - creates SearchParams object with given
 * facets and title. Interposes OR between words in provided title for
 * keyword created searches over 4 words long - restored in WWF-185
 * @param facets
 * @param title
 * @returns SearchParams
 */
export const getOnTheHuntItemSearchParams = (facets: ItemFacets, title: string = '') => {
    const params: SearchParams = {};
    const MAX_WORDS = 4;

    if (facets?.creators?.length) {
        params.category_id = facets.creators[0].l1CategoryId?.toString();
    } else {
        params.keyword = addOrsBetweenKeywords(cleanKeywordSearchString(title), MAX_WORDS);
    }
    return params;
};

type SimilarItemsSearchParams = {
    house_id?: string;
    keyword?: string;
    status: SearchQueryStatus[];
};

/**
 * Builds the multi-status search URL used to surface similar items. The multi-status
 * filter is a similar-items-only concern, so it keeps its own param shape rather than
 * widening `SearchUrlParams.status` (which stays single-valued for the rest of the app).
 */
const getSimilarItemsSearchUrl = (params: SimilarItemsSearchParams): string => {
    return buildQueryUrl(getSearchBaseUrl(), params);
};

export const getSimilarItemsUrl = (title: string = '', sellerId: number = 0) => {
    const { house_id, keyword } = getBroadTitleSearchParams(title, sellerId);
    const params: SimilarItemsSearchParams = {
        keyword,
        status: [SearchQueryStatus.Online, SearchQueryStatus.Live, SearchQueryStatus.Upcoming],
    };
    if (house_id) {
        params.house_id = house_id;
    }
    return getSimilarItemsSearchUrl(params);
};

export const getCoverLots = (coverLots: CoverLot[], height?: number) => {
    const [first, second, third] = coverLots.length ? coverLots : [EmptyCoverLot, EmptyCoverLot, EmptyCoverLot];
    const primaryImage = first?.url
        ? addImageParams({
              height: height || 260,
              url: first?.url,
              version: first?.imageVersion,
          })
        : imagePlaceholder;
    const primaryImageHighDpi = first?.url
        ? addImageParams({
              height: height * 2 || 520,
              url: first?.url,
              version: first?.imageVersion,
          })
        : imagePlaceholder;
    const primaryItemUrl = getItemUrl(first?.itemId, first?.title);

    const secondaryImage = second?.url
        ? addImageParams({
              height: height || (coverLots?.length === 2 ? 260 : 130),
              url: second?.url,
              version: second?.imageVersion,
          })
        : imagePlaceholder;
    const secondaryImageHighDpi = second?.url
        ? addImageParams({
              height: height * 2 || (coverLots?.length === 2 ? 520 : 260),
              url: second?.url,
              version: second?.imageVersion,
          })
        : imagePlaceholder;
    const secondaryItemUrl = getItemUrl(second?.itemId, second?.title);

    const tertiaryImage = third?.url
        ? addImageParams({
              height: height || 130,
              url: third?.url,
              version: third?.imageVersion,
          })
        : imagePlaceholder;
    const tertiaryImageHighDpi = third?.url
        ? addImageParams({
              height: height * 2 || 260,
              url: third?.url,
              version: third?.imageVersion,
          })
        : imagePlaceholder;
    const tertiaryItemUrl = getItemUrl(third?.itemId, third?.title);

    return {
        // image src
        primaryImage,
        // high-res image src
        primaryImageHighDpi,
        // item id
        primaryItemId: first?.itemId,
        // item title
        primaryItemTitle: first?.title,
        // link to item page
        primaryItemUrl,

        // image src
        secondaryImage,
        // high-res image src
        secondaryImageHighDpi,
        // item id
        secondaryItemId: second?.itemId,
        // item title
        secondaryItemTitle: second?.title,
        // link to item page
        secondaryItemUrl,

        // image src
        tertiaryImage,
        // high-res image src
        tertiaryImageHighDpi,
        // item id
        tertiaryItemId: third?.itemId,
        // item title
        tertiaryItemTitle: third?.title,
        // link to item page
        tertiaryItemUrl,
    };
};

const getWebviewsUrl = (path: string, deployment: string) => {
    let domain = `https://webviews${deployment === 'prod' ? '' : `-${deployment}`}.liveauctioneers.com`;
    // domain = 'https://localdev.liveauctioneers.com:3002';
    return `${domain}/${path}`;
};

export const getPayrixAddCreditCardUrl = (
    deployment: string,
    address: any,
    authToken?: string,
    showSignUpFlow?: boolean
): string => {
    let url = getWebviewsUrl('addcreditcard/3', deployment);
    let queryParam = '';

    if (address) {
        queryParam = `address1=${encodeURIComponent(address.address1)}&address2=${encodeURIComponent(
            address.address2
        )}&state=${encodeURIComponent(address.state)}&countryCode=${encodeURIComponent(
            address.countryCode
        )}&city=${encodeURIComponent(address.city)}&postalCode=${encodeURIComponent(address.postalCode)}`;
    }
    if (authToken) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}token=${authToken}`;
    }
    if (showSignUpFlow) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}showSignUpFlow=1`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }
    return url;
};

export type TaxCertCaptureUserData = {
    address: string;
    city: string;
    country: string;
    email: string;
    name: string;
    phone: string;
    state: string;
    zip: string;
};

export const getTaxCertCaptureUrl = (
    deployment: string,
    token: string,
    shipZone: string,
    userData: TaxCertCaptureUserData,
    bidderId: number
) => {
    // exit with a falsy value if we are missing data.
    if (!token || !shipZone) {
        return '';
    }
    let url = getWebviewsUrl('taxcertcapture', deployment);
    let queryParam = '';
    if (token && shipZone) {
        queryParam = `token=${token}&shipZone=${shipZone}`;
    }
    if (token && shipZone) {
        queryParam = `${queryParam}&bidderId=${bidderId}`;
    }
    if (userData) {
        queryParam = `${queryParam}&userData=${encodeURIComponent(JSON.stringify(userData))}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }

    return url;
};

export const getCertCapturePageUrl = (shipZone: string) => {
    const url = '/certcapture';
    let queryParam = '';
    if (shipZone) {
        queryParam = `?shipZone=${shipZone}`;
    }
    return `${url}${queryParam}`;
};

/**
 * OLD params type (deprecated but still supported)
 * @deprecated Use GeoArea directly instead
 */
export type AuctionsNearMeGeoAreaParams = {
    geoAreaId: number;
    geoAreaName: string;
};

/**
 * Generate URL for Auctions Near Me page
 * @param geoAreaData - GeoArea object or legacy params with geoAreaId/geoAreaName
 * @returns URL path for the Auctions Near Me page
 *
 * @example
 * // SEO-friendly format with full GeoArea
 * getAuctionsNearMePageUrl(denverGeoArea)
 * // => '/find-auctions-near-me/co/denver/'
 *
 * // No geo area (base page)
 * getAuctionsNearMePageUrl()
 * // => '/find-auctions-near-me/'
 *
 * // Legacy params (falls back to old format for backward compatibility)
 * getAuctionsNearMePageUrl({ geoAreaId: 20, geoAreaName: 'Denver' })
 * // => '/find-auctions-near-me/20/denver/'
 */
export const getAuctionsNearMePageUrl = (geoAreaData?: GeoArea | AuctionsNearMeGeoAreaParams): string => {
    const baseUrl = '/find-auctions-near-me';

    if (!geoAreaData) {
        return `${baseUrl}/`;
    }

    // SEO-friendly URL format (requires full GeoArea with location data)
    // Note: Empty string is valid for country/state-level GeoAreas
    if ('locality' in geoAreaData && geoAreaData.locality !== null) {
        const urlPath = geoAreaToUrlPath(geoAreaData);
        return `${baseUrl}${urlPath}`;
    }

    // Old URL format (backward compatibility for legacy params)
    const geoAreaId = geoAreaData.geoAreaId;
    const geoAreaName = geoAreaData.geoAreaName;
    return `${baseUrl}/${geoAreaId}/${normalizeForUrl(geoAreaName)}/`;
};

export const isAuctionsNearMePage = (path: string = '') => path.includes('/find-auctions-near-me/');

/**
 * Generate URL for custom location search on Auctions Near Me page
 * Used when user searches for a location that isn't a predefined GeoArea
 * @param searchTerm - The location search term (e.g., "Cleveland")
 * @returns URL path with search query parameter
 *
 * @example
 * getAuctionsNearMeSearchUrl('Cleveland')
 * // => '/find-auctions-near-me/?search=Cleveland'
 *
 * getAuctionsNearMeSearchUrl('New Albany')
 * // => '/find-auctions-near-me/?search=New%20Albany'
 */
export const getAuctionsNearMeSearchUrl = (searchTerm: string): string => {
    const baseUrl = '/find-auctions-near-me/';
    const encodedSearchTerm = encodeURIComponent(searchTerm);
    return `${baseUrl}?search=${encodedSearchTerm}`;
};

export const getShippingPartnerPolicyUrl = (partnerId: keyof typeof shippingProviders): string => {
    const baseUrl = `/shipping-policies/${partnerId}`;
    switch (Number(partnerId)) {
        case ShippingPartnerIds.ThePackengers:
            return `${baseUrl}/thepackengers`;
        case ShippingPartnerIds.NationalLogisticServices:
            return `${baseUrl}/national-logistic-services`;
        case ShippingPartnerIds.ChairishAuctions:
            return `${baseUrl}/chairish-auctions`;
        default:
            return '';
    }
};

type GoogleMapAddress = {
    city: string;
    country: string;
    region: string;
};

export const getGoogleMapUrlFromAddress = ({ city, country, region }: GoogleMapAddress) => {
    const location = [city, region, country]
        // remove duplicate whitespaces then trim off extra spaces from the ends before we encode
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
    const encodedLocation = encodeURIComponent(location);
    return `https://www.google.com/maps/place/${encodedLocation}`;
};

/**
 * Returns a string url from a location object
 * @param location Location<unknown> object
 * @param baseUrl string
 * @returns url string of the location + baseUrl
 */
export const buildUrlFromLocation = (location, baseUrl?: string) => {
    const url = `${baseUrl || ''}${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;
    return url === '' ? undefined : url;
};

export const removeTrailingSlash = (url: string) => (url?.[url?.length - 1] === '/' ? url.slice(0, -1) : url);

/**
 * Appends a trailing slash when one is missing. Required for rel=canonical and rel=prev/next:
 * the server 302s any slash-less path as soon as a query string is appended, so a slash-less
 * canonical turns every `?page=N` link built from it into a redirect.
 */
export const addTrailingSlash = (path: string = '') => (path.endsWith('/') ? path : `${path}/`);

export const removeScheme = (url: string) => url?.replace('https://', '').replace('http://', '') || url;

export const getAreUrlsEqual = (firstUrl: string, secondUrl: string) => {
    if (typeof firstUrl !== 'string' || typeof secondUrl !== 'string') {
        return false;
    }
    const firstUrlWithoutQuery = firstUrl?.split('?')?.[0];
    const secondUrlWithoutQuery = secondUrl?.split('?')?.[0];

    const firstWithoutScheme = removeScheme(firstUrlWithoutQuery);
    const secondWithoutScheme = removeScheme(secondUrlWithoutQuery);

    return removeTrailingSlash(firstWithoutScheme) === removeTrailingSlash(secondWithoutScheme);
};

export type PathLocale = {
    /** parsed locale, if available */
    locale: string;
    /** parsed locale with / prefix */
    localePath: string;
    /** remaining path with locale prefix removed */
    path: string;
};

/**
 * parsePathLocale - parses locale string out of incoming path string and returns object
 * with parts returned in locale and path
 * if no locale is parsed, locale and localePath are blank string, path is what was sent
 * @param path string
 * @returns PathLocale
 */
export const parsePathLocale = (path: string): PathLocale => {
    const match = path.match(/^\/(\w{2}-\w{2})\W*/);
    if (match?.[1]) {
        const localePath = `/${match[1]}`;
        return {
            locale: match[1],
            localePath,
            path: path.replace(localePath, ''),
        };
    }
    return { locale: '', localePath: '', path };
};
